<template>
  <DialogWrapper
    destroyOnClose
    class="hr-interview-confirm-dialog"
    :show.sync="show"
    :loading="loading"
    :closeOnClickModal="false"
    @closed="handleClosedDialog"
  >
    <div class="hr-interview-confirm-form">
      <Step :stepList="stepList" :activeIndex="activeIndex">
      </Step>
      <StepContent class="step-content" :stepList="stepList" :activeIndex="activeIndex">
        <template #invitation="{ step }">
          <div class="content-label">{{ step.contentLabel }}</div>
          <div class="microsite-select-container" v-if="hasCRMMicrositeCampaignPermission">
            <span class="microsite-label">{{ $t(`${i18nPrefix}.label.microsite`) }}</span>
            <el-cascader
              v-model="selectMicrosite"
              class="microsite-select"
              popper-class="microsite-select-popper"
              disabled
              clearable
              :placeholder="$t(`${i18nPrefix}.placeholder.microsite`)"
              :show-all-levels="false"
              :options="micrositeGroupList"
              :props="{ expandTrigger: 'hover', value: 'id' }">
            </el-cascader>
          </div>
          <div class="invitation hm">
            <div class="label">
              <div class="to-select-language">
                <span class="send-type">{{ $t(`${i18nPrefix}.label.sendToHM`) }}</span>
                <span class="language-title">{{ $t(`${i18nPrefix}.placeholder.interviewEmailTemplateLanguage`) }}</span>
                <el-select v-model="sendToHmWithLanguage" class="language-select" disabled :placeholder="$t(`${i18nPrefix}.placeholder.interviewEmailTemplateLanguage`)" @change="handleSendHmLanguageChange">
                  <el-option
                    v-for="(item, index) in languageList"
                    :key="index"
                    :label="item.label"
                    :value="item.value"
                  ></el-option>
                </el-select>
              </div>
              <el-switch
                v-model="isSendToHmEmail"
                disabled
                active-color="#0096cc"
                inactive-color="#ccc"
              ></el-switch>
            </div>
            <div class="email-template-content" v-loading="loadingHmTemplate">
              <div class="invitation-subject">
                <div class="subject-label">{{ $t(`${i18nPrefix}.label.invitationSubject`) }}</div>
                <el-input v-model="hmSubjectContent" disabled></el-input>
              </div>
              <TinymceEditor
                v-if="showHmEmailTemplate"
                v-model="hmTemplateContent"
                class="invitation-content"
                disabled
                showTemplateField
                :showGenerateAISummaryButton="hasAISummaryPermission"
                :showGenerateAIMatchButton="hasAIResumeMatchPermission"
                :applicationList="applicationList"
                :emailLanguage="sendToHmWithLanguage"
                :aiTemplateField="AI_SUMMARY_TEMPLATE_FIELD"
                :themeColor="emailThemeColor"
                @generateAISummary="handleGenerateAISummary"
                @generateAIMatch="handleGenerateAIMatch"
                @enableOperateBtn="handleEnableOperateBtn"
              >
              </TinymceEditor>
            </div>
          </div>
          <div class="invitation candidate">
            <div class="label">
              <div class="to-select-language">
                <span class="send-type">{{ $t(`${i18nPrefix}.label.sendToCandidate`) }}</span>
                <span class="language-title">{{ $t(`${i18nPrefix}.placeholder.interviewEmailTemplateLanguage`) }}</span>
                <el-select v-model="sendToCandidateWithLanguage" class="language-select" disabled :placeholder="$t(`${i18nPrefix}.placeholder.interviewEmailTemplateLanguage`)">
                  <el-option
                    v-for="(item, index) in languageList"
                    :key="index"
                    :label="item.label"
                    :value="item.value"
                  ></el-option>
                </el-select>
              </div>
              <el-switch
                v-model="isSendToCandidateEmail"
                disabled
                active-color="#0096cc"
                inactive-color="#ccc"
              ></el-switch>
            </div>
            <div class="email-template-content" v-loading="loadingCandidateTemplate">
              <div class="invitation-subject">
                <div class="subject-label">{{ $t(`${i18nPrefix}.label.invitationSubject`) }}</div>
                <el-input v-model="candidateSubjectContent" disabled></el-input>
              </div>
              <TinymceEditor
                v-if="showCandidateEmailTemplate"
                v-model="candidateTemplateContent"
                class="invitation-content"
                disabled
                :showTemplateField="true"
                :themeColor="emailThemeColor"
              >
              </TinymceEditor>
            </div>
          </div>
        </template>
      </StepContent>
    </div>
    <template #footer>
      <div class="dialog-footer">
        <el-button class="footer-btn cancel-btn" :disabled="disabledHmOperateBtn" @click="handleCancel">{{ $t('common.btn.cancel') }}</el-button>
        <el-button class="footer-btn save-btn" :disabled="previewInterviewEmailTypeList.length === 0 || disabledHmOperateBtn" @click="handleOpenPreviewEmailDialog">{{ $t(`${i18nPrefix}.btn.previewEmail`) }}</el-button>
        <el-button class="footer-btn save-btn" :disabled="disabledHmOperateBtn" @click="handleSave">{{ $t(saveBtnLabelI18nKey) }}</el-button>
      </div>
    </template>
    <DialogWrapper
      class="preview-interview-email-dialog"
      appendToBody
      hideFooter
      fullscreen
      :show.sync="showPreviewInterviewEmailDialog"
      :loading="previewInterviewEmailLoading"
      :title="$t(`${i18nPrefix}.dialogTitle.previewInterviewEmailDialogTitle`)"
    >
      <div class="header">
        <el-select
          v-model="previewInterviewEmailType"
          class="select-preview-type"
          @change="handleChangePreviewType"
        >
          <el-option
            v-for="(item, index) in previewInterviewEmailTypeList"
            :label="item.label"
            :value="item.value"
            :key="index"
          ></el-option>
        </el-select>
        <div class="operation-btn">
          <el-button class="footer-btn cancel-btn" @click="showPreviewInterviewEmailDialog = false">{{ $t('common.btn.cancel') }}</el-button>
          <el-button class="footer-btn save-btn" @click="handleSendEmail">{{ $t('common.btn.send') }}</el-button>
        </div>
      </div>
      <div v-html="previewInterviewEmailContent" v-loading="loadingInterviewEmail" class="preview-content"></div>
    </DialogWrapper>
  </DialogWrapper>
</template>

<script>
import _ from 'lodash'
import { mapActions } from 'vuex'
import moment from 'moment'
import { addI18nProperty, getI18nPropertyValue } from '@/i18n'
import { MICROSITE_PATH } from '@/constant/crm'

import StepContent from '../stepContent.vue'
import Step from '@/components/step/stepMenu.vue'
import DialogWrapper from '../../../../components/dialog/dialogWrapper.vue'
import TinymceEditor from '../../application/components/tinymceEditor.vue'
import { openNewBrowserWindowByUrl, AsyncTask } from '@/utils/common'

import { DEFAULT_TIME_ZONE, INTERVIEW_TYPE_ONLINE_TEAMS_MEETING } from '@/constant/interview'

const NAME_LIST = {
  HM_SELECTOR: 'HM_SELECTOR',
  HM_COMMENT: 'HM_COMMENT',
  INTERVIEW_RATING_FORM: 'INTERVIEW_RATING_FORM',
  TIME_AND_LOCATION: 'TIME_AND_LOCATION',
  EMAIL_EDITOR: 'EMAIL_EDITOR'
}

const HM_ON_SITE_TEMPLATE_TYPE = 'Onsite Interview Invitation Email (HM)'
const CANDIDATE_ON_SITE_TEMPLATE_TYPE = 'Onsite Interview Invitation Email (Candidate)'
const HM_ONLINE_TEMPLATE_TYPE = 'Online Interview Invitation Email (HM)'
const CANDIDATE_ONLINE_TEMPLATE_TYPE = 'Online Interview Invitation Email (Candidate)'

// 在邮件模板中使用的占位符，AI summary 将在这个位置插入
const AI_SUMMARY_TEMPLATE_FIELD = '{{ Candidate Info Table }}'

export default {
  components: {
    Step,
    DialogWrapper,
    StepContent,
    TinymceEditor
  },
  props: {
    applicationList: {
      required: true
    }
  },
  metaInfo () {
    return {
      style: [
        { innerHTML: '.el-cascader__dropdown.el-popper.microsite-select-popper .el-cascader-menu{max-width: 300px;}' }
      ]
    }
  },
  data () {
    return {
      AI_SUMMARY_TEMPLATE_FIELD,
      i18nPrefix: 'admin.application.applicantList',
      show: false,
      loading: false,
      stepLoading: false,
      activeIndex: 0,
      isSendToHmEmail: true,
      isSendToCandidateEmail: true,
      selectHiringList: [],
      timeZone: DEFAULT_TIME_ZONE,
      onlineInfo: {
        url: ''
      },
      location: null,
      locationList: [],
      NAME_LIST,
      selectMicrosite: null,
      micrositeList: [],
      sendToHmWithLanguage: 'zh-cn',
      sendToCandidateWithLanguage: 'zh-cn',
      hmSubjectCnContent: null,
      hmSubjectEnContent: null,
      hmTemplateCnContent: null,
      hmTemplateEnContent: null,
      candidateSubjectCnContent: null,
      candidateSubjectEnContent: null,
      candidateTemplateCnContent: null,
      candidateTemplateEnContent: null,
      emailThemeColor: '#0096cc',
      loadingHmTemplate: false,
      loadingCandidateTemplate: false,
      previewInterviewEmailType: 0,
      showPreviewInterviewEmailDialog: false,
      previewInterviewEmailLoading: false,
      loadingInterviewEmail: false,
      previewInterviewEmailContent: null,
      teamsMeetingUrl: null,
      teamsMeetingId: null,
      teamsMeetingPassword: null,
      hasMicrosoftGraphAuthToken: false,
      microsoftGraphAuthUrl: null,
      microsoftGraphEventInfo: {
        oldMicrosoftGraphEventId: null,
        newMicrosoftGraphEventId: null
      },
      needSaveMicrosoftOnlineMeeting: false,
      interviewUuid: null,
      hmSendEmailRecordInfo: null,
      candidateSendEmailRecordInfo: null,
      showHmEmailTemplate: false,
      showCandidateEmailTemplate: false,
      emailTemplateLoadVersion: 0,
      disabledHmOperateBtn: false,
      interviewedTime: null,
      meetingRoom: null
    }
  },
  computed: {
    previewInterviewEmailTypeList () {
      const list = []
      if (this.isSendToHmEmail) {
        list.push({
          label: this.$t(`${this.i18nPrefix}.label.previewHmEmail`),
          value: 0
        })
      }

      if (this.isSendToCandidateEmail) {
        list.push({
          label: this.$t(`${this.i18nPrefix}.label.previewCandidateEmail`),
          value: 1
        })
      }
      return list
    },
    languageList () {
      const list = [
        {
          label: this.$t('common.label.english'),
          value: 'en'
        },
        {
          label: this.$t('common.label.chinese'),
          value: 'zh-cn'
        }
      ]
      return list
    },
    hmSubjectContent: {
      get () {
        if (this.hasSendHmEmailInfo) {
          return this.hasSendHmEmailInfo.subject
        }
        return this.sendToHmWithLanguage === 'en' ? this.hmSubjectEnContent : this.hmSubjectCnContent
      },
      set (value) {
        if (this.hasSendHmEmailInfo) {
          this.hasSendHmEmailInfo.subject = value
        }
        if (this.sendToHmWithLanguage === 'en') {
          this.hmSubjectEnContent = value
        } else {
          this.hmSubjectCnContent = value
        }
      }
    },
    hmTemplateContent: {
      get () {
        if (this.hasSendHmEmailInfo) {
          return this.hasSendHmEmailInfo.content
        }
        return this.sendToHmWithLanguage === 'en' ? this.hmTemplateEnContent : this.hmTemplateCnContent
      },
      set (value) {
        if (this.hasSendHmEmailInfo) {
          this.hasSendHmEmailInfo.content = value
        }
        if (this.sendToHmWithLanguage === 'en') {
          this.hmTemplateEnContent = value
        } else {
          this.hmTemplateCnContent = value
        }
      }
    },
    candidateSubjectContent: {
      get () {
        if (this.hasSendCandidateEmailInfo) {
          return this.hasSendCandidateEmailInfo.subject
        }
        return this.sendToCandidateWithLanguage === 'en' ? this.candidateSubjectEnContent : this.candidateSubjectCnContent
      },
      set (value) {
        if (this.hasSendCandidateEmailInfo) {
          this.hasSendCandidateEmailInfo.subject = value
        }
        if (this.sendToCandidateWithLanguage === 'en') {
          this.candidateSubjectEnContent = value
        } else {
          this.candidateSubjectCnContent = value
        }
      }
    },
    candidateTemplateContent: {
      get () {
        if (this.hasSendCandidateEmailInfo) {
          return this.hasSendCandidateEmailInfo.content
        }
        return this.sendToCandidateWithLanguage === 'en' ? this.candidateTemplateEnContent : this.candidateTemplateCnContent
      },
      set (value) {
        if (this.hasSendCandidateEmailInfo) {
          this.hasSendCandidateEmailInfo.content = value
        }
        if (this.sendToCandidateWithLanguage === 'en') {
          this.candidateTemplateEnContent = value
        } else {
          this.candidateTemplateCnContent = value
        }
      }
    },
    hasSendHmEmailInfo () {
      if (!this.hmSendEmailRecordInfo) {
        return
      }
      const info = _.get(this.hmSendEmailRecordInfo, this.sendToHmWithLanguage)
      return info
    },
    hasSendCandidateEmailInfo () {
      if (!this.candidateSendEmailRecordInfo) {
        return
      }
      const info = _.get(this.candidateSendEmailRecordInfo, this.sendToCandidateWithLanguage)
      return info
    },
    stepList () {
      const step = [
        {
          name: NAME_LIST.EMAIL_EDITOR,
          title: this.$t(`${this.i18nPrefix}.title.sendInvitation`),
          contentLabel: this.$t(`${this.i18nPrefix}.label.confirmInterview`),
          contentSlot: 'invitation'
        }
      ]
      return _.filter(step, item => !item.hidden)
    },
    saveBtnLabelI18nKey () {
      return `common.btn.${this.activeIndex === this.stepList.length - 1 ? 'send' : 'next'}`
    },
    hasCRMMicrositeCampaignPermission () {
      return this.hasAnyPermission('PREMIUM_MICROSITE_CAMPAIGN_MANAGEMENT')
    },
    micrositeGroupList () {
      const micrositeList = this.micrositeList
      const groupList = []
      _.forEach(_.groupBy(micrositeList, 'group.id'), (value, key) => {
        if (key !== 'undefined') {
          const group = value[0].group
          groupList.push({
            label: group.i18nName,
            id: group.id,
            children: value
          })
        } else {
          groupList.push({
            label: '未分组',
            id: 0,
            children: value
          })
        }
      })
      return groupList
    },
    hasAISummaryPermission () {
      return this.hasAnyPermission('PREMIUM_AI_SUMMARY')
    },
    hasAIResumeMatchPermission () {
      return this.hasAnyPermission(['PREMIUM_AI_RESUME_MATCH', 'PERM_HR_VIEW_AI_FIT_SCORE'])
    }
  },
  created () {
    this.selectedDate = moment().format('YYYY-MM-DD')
  },
  methods: {
    ...mapActions('application', ['getApplicationAISummary', 'getApplicationAIMatchInfo']),
    ...mapActions('crm', ['searchMicrosite']),
    ...mapActions('jobManagement', ['getJobLocationList']),
    ...mapActions('notificationTemplate', ['getCompanyInterviewEmailTemplate']),
    ...mapActions('interview', ['previewInterviewEmailInfo', 'getInterviewInvitationRecordInfo', 'updateInterviewRecordOnlineInfo']),
    ...mapActions('microsoftGraph', ['checkMicrosoftGraphAuth', 'createOnlineMeetingEvent', 'updateOnlineMeetingEvent', 'deleteOnlineMeetingEvent']),
    ...mapActions('ai', ['createAISummary', 'getAISummaryInfoByTaskId', 'createAIResumeMatch', 'getAIMatchInfoByTaskId']),
    async loadApplicationAISummary () {
      const params = {
        job_id: this.applicationList[0].job.id,
        candidate_ids: _.map(this.applicationList, item => item.candidate.id).join(',')
      }
      const resp = await this.getApplicationAISummary(params)
      if (resp) {
        _.forEach(resp, (aiSummaryInfo, candidateId) => {
          const application = _.find(this.applicationList, item => item.candidate.id === _.parseInt(candidateId))
          const index = _.findIndex(this.applicationList, item => item.candidate.id === _.parseInt(candidateId))
          application.candidate.ai_summary_info = aiSummaryInfo
          this.applicationList.splice(index, 1, application)
        })
      }
    },
    async loadApplicationAIMatchInfo () {
      const params = {
        job_id: this.applicationList[0].job.id,
        candidate_ids: _.map(this.applicationList, item => item.candidate.id).join(',')
      }
      const resp = await this.getApplicationAIMatchInfo(params)
      if (resp) {
        _.forEach(resp, (aiMatchInfo, candidateId) => {
          const application = _.find(this.applicationList, item => item.candidate.id === _.parseInt(candidateId))
          const index = _.findIndex(this.applicationList, item => item.candidate.id === _.parseInt(candidateId))
          application.candidate.ai_match_info = aiMatchInfo
          this.applicationList.splice(index, 1, application)
        })
      }
    },
    handleOpenPreviewEmailDialog () {
      this.showPreviewInterviewEmailDialog = true
      this.handleChangePreviewType()
    },
    async handleChangePreviewType () {
      this.previewInterviewEmailContent = null
      const duration = moment(this.interviewedTime.endTime, 'hh:mm').diff(moment(this.interviewedTime.startTime, 'hh:mm'), 'minutes')
      const startTime = `${this.interviewedTime.startDate} ${this.interviewedTime.startTime}`
      const templateContent = this.previewInterviewEmailType ? this.candidateTemplateContent : this.hmTemplateContent
      const languageCode = this.previewInterviewEmailType ? this.sendToCandidateWithLanguage : this.sendToHmWithLanguage
      if (!templateContent) {
        return
      }
      const meetingUrl = this.teamsMeetingUrl
      const meetingID = this.teamsMeetingId
      const meetingPassword = this.teamsMeetingPassword
      const params = {
        is_candidate: this.previewInterviewEmailType,
        template_content: templateContent,
        interview_start_time: startTime,
        duration: duration,
        job_id: this.applicationList[0].job.id,
        candidate_ids: _.map(this.applicationList, item => item.candidate.id),
        hm_info_list: this.selectHiringList,
        interview_address: this.location?.value || this.location,
        interview_type: INTERVIEW_TYPE_ONLINE_TEAMS_MEETING,
        timezone: this.timeZone,
        is_online: true,
        online_meeting_info: {
          url: meetingUrl,
          address: this.location?.value || this.location,
          meeting_id: meetingID,
          meeting_password: meetingPassword
        },
        microsite_id: this.selectMicrosite ? this.selectMicrosite[1] : '',
        language_code: languageCode,
        theme_color: this.emailThemeColor
      }
      this.loadingInterviewEmail = true
      try {
        const resp = await this.previewInterviewEmailInfo(params)
        if (resp) {
          this.previewInterviewEmailContent = resp
        }
      } finally {
        this.loadingInterviewEmail = false
      }
    },
    async loadHmEmailTemplate () {
      const isOnline = true
      let hmTemplateType = null
      if (isOnline) {
        hmTemplateType = HM_ONLINE_TEMPLATE_TYPE
      } else {
        hmTemplateType = HM_ON_SITE_TEMPLATE_TYPE
      }
      return this.getCompanyInterviewEmailTemplate({
        email_template_type: hmTemplateType,
        job_id: this.applicationList[0].job.id
      })
    },
    async loadCandidateEmailTemplate () {
      const isOnline = true
      let candidateTemplateType = null
      if (isOnline) {
        candidateTemplateType = CANDIDATE_ONLINE_TEMPLATE_TYPE
      } else {
        candidateTemplateType = CANDIDATE_ON_SITE_TEMPLATE_TYPE
      }
      return this.getCompanyInterviewEmailTemplate({
        email_template_type: candidateTemplateType,
        job_id: this.applicationList[0].job.id
      })
    },
    async loadHasSendEmailContent () {
      if (!this.interviewUuid) {
        return null
      }
      const params = {
        interview_uuid: this.interviewUuid
      }
      return this.getInterviewInvitationRecordInfo(params)
    },
    invalidateEmailTemplateLoad () {
      this.emailTemplateLoadVersion += 1
      this.showHmEmailTemplate = false
      this.showCandidateEmailTemplate = false
      this.loadingHmTemplate = false
      this.loadingCandidateTemplate = false
    },
    async prepareEmailTemplates () {
      const loadVersion = this.emailTemplateLoadVersion + 1
      this.emailTemplateLoadVersion = loadVersion
      this.showHmEmailTemplate = false
      this.showCandidateEmailTemplate = false
      this.loadingHmTemplate = true
      this.loadingCandidateTemplate = true
      this.emailThemeColor = '#0096cc'

      const promiseList = [
        this.loadHmEmailTemplate(),
        this.loadCandidateEmailTemplate(),
        this.loadHasSendEmailContent()
      ]
      if (this.hasAISummaryPermission) {
        promiseList.push(this.loadApplicationAISummary())
      }
      if (this.hasAIResumeMatchPermission) {
        promiseList.push(this.loadApplicationAIMatchInfo())
      }

      let hmTemplateResp = null
      let candidateTemplateResp = null
      try {
        const respList = await Promise.all(promiseList)
        if (loadVersion !== this.emailTemplateLoadVersion) {
          return
        }
        hmTemplateResp = respList[0]
        candidateTemplateResp = respList[1]
        const emailRecordResp = respList[2]

        if (hmTemplateResp) {
          this.hmSubjectCnContent = hmTemplateResp.subject_cn
          this.hmSubjectEnContent = hmTemplateResp.subject_en
          this.hmTemplateCnContent = hmTemplateResp.content_cn
          this.hmTemplateEnContent = hmTemplateResp.content_en
          this.emailThemeColor = hmTemplateResp.theme_color || '#0096cc'
        }
        if (candidateTemplateResp) {
          this.candidateSubjectCnContent = candidateTemplateResp.subject_cn
          this.candidateSubjectEnContent = candidateTemplateResp.subject_en
          this.candidateTemplateCnContent = candidateTemplateResp.content_cn
          this.candidateTemplateEnContent = candidateTemplateResp.content_en
          this.emailThemeColor = candidateTemplateResp.theme_color || this.emailThemeColor
        }
        this.hmSendEmailRecordInfo = emailRecordResp ? emailRecordResp.HM : null
        this.candidateSendEmailRecordInfo = emailRecordResp ? emailRecordResp.CANDIDATE : null
      } finally {
        if (loadVersion === this.emailTemplateLoadVersion) {
          this.loadingHmTemplate = false
          this.loadingCandidateTemplate = false
        }
      }

      if (loadVersion !== this.emailTemplateLoadVersion) {
        return
      }
      this.showHmEmailTemplate = Boolean(hmTemplateResp)
      this.showCandidateEmailTemplate = Boolean(candidateTemplateResp)
      await this.$nextTick()
      if (loadVersion === this.emailTemplateLoadVersion) {
        this.resetTinymceDialogStyle()
      }
    },
    async showDialog (defaultInvitationIfo) {
      this.invalidateEmailTemplateLoad()
      this.show = true
      this.stepLoading = true
      await this.$nextTick()
      this.activeIndex = 0
      this.hmSendEmailRecordInfo = null
      this.candidateSendEmailRecordInfo = null
      this.selectMicrosite = null
      this.onlineInfo.url = null
      this.hasMicrosoftGraphAuthToken = false
      this.microsoftGraphAuthUrl = null
      this.microsoftGraphEventInfo = {
        oldMicrosoftGraphEventId: null,
        newMicrosoftGraphEventId: null
      }
      this.teamsMeetingUrl = null
      this.teamsMeetingId = null
      this.teamsMeetingPassword = null
      this.needSaveMicrosoftOnlineMeeting = false
      this.meetingRoom = defaultInvitationIfo.meeting_room

      const companyInfo = this.applicationList[0].job.company

      const isHsbcCompany = companyInfo.root_id === 6429 || companyInfo.root_company_id === 6429
      if (isHsbcCompany) {
        this.isSendToHmEmail = false
      }

      this.interviewUuid = defaultInvitationIfo.uuid
      const micrositeInfo = defaultInvitationIfo.microsite_info
      if (micrositeInfo) {
        this.selectMicrosite = [micrositeInfo.group_id, micrositeInfo.id]
      }
      this.location = _.find(this.locationList, { value: defaultInvitationIfo.interview_address }) || defaultInvitationIfo.interview_address
      this.selectHiringList = _.map(defaultInvitationIfo.hire_manager, hm => {
        return {
          ...hm,
          email: hm.email_address,
          company: hm.company_id
        }
      })
      this.interviewedTime = {
        startDate: moment(defaultInvitationIfo.interview_time).format('YYYY-MM-DD'),
        startTime: moment(defaultInvitationIfo.interview_time).format('HH:mm:ss'),
        endDate: moment(defaultInvitationIfo.interview_end_time).format('YYYY-MM-DD'),
        endTime: moment(defaultInvitationIfo.interview_end_time).format('HH:mm:ss')
      }
      this.timezone = defaultInvitationIfo.timezone
      this.microsoftGraphEventInfo.oldMicrosoftGraphEventId = defaultInvitationIfo.microsoft_graph_event_id
      this.onlineInfo.url = null

      if (this.hasCRMMicrositeCampaignPermission) {
        this.loadMicrositeList()
      }
      this.sendToHmWithLanguage = this.$i18n.locale === 'en' ? 'en' : 'zh-cn'
      this.sendToCandidateWithLanguage = this.$i18n.locale === 'en' ? 'en' : 'zh-cn'

      this.checkCurrentAccountAuthGraph()
      this.loadLocationList()
      try {
        await this.prepareEmailTemplates()
      } finally {
        this.stepLoading = false
      }
    },
    async loadMicrositeList () {
      const resp = await this.searchMicrosite({ is_published: 1 })
      if (resp) {
        const url = `${window.location.origin}${MICROSITE_PATH.INDEX}`
        this.micrositeList = _.map(resp, item => {
          addI18nProperty(item.group, 'i18nName', 'cn_name', 'name')
          return {
            ...item,
            label: item.name,
            link: `${url}${item.short_unique_id}`
          }
        })
      }
    },
    async loadLocationList () {
      const params = {
        job_id: _.map(this.applicationList, item => item.job.id).join(',')
      }
      const resp = await this.getJobLocationList(params)
      if (resp) {
        this.locationList = _.map(resp, location => {
          Object.defineProperty(location, 'value', {
            configurable: true,
            enumerable: true,
            get () {
              const cityInfo = _.chain(['city', 'region', 'cbd'])
                .map(key => {
                  const val = location[key]
                  if (!val) {
                    return
                  }
                  return getI18nPropertyValue(val, 'cn_name', 'name')
                })
                .filter()
                .join(' ')
                .value() || ''
              return `${cityInfo}${cityInfo ? ' ' : ''}${location.address || ''}`
            }
          })
          return location
        })
      }
    },
    async handleSave () {
      if (!this.hasMicrosoftGraphAuthToken) {
        await this.checkCurrentAccountAuthGraph()
        return
      }
      this.needSaveMicrosoftOnlineMeeting = true
      if (!this.teamsMeetingUrl) {
        await this.createOrUpdateMicrosoftMeeting()
      }
      if (this.loading) {
        return
      }
      this.loading = true
      const data = {
        interviewUuid: this.interviewUuid,
        data: {
          online_info: {
            provider: 'teams',
            url: this.teamsMeetingUrl,
            address: this.location?.value || this.location,
            meeting_id: this.teamsMeetingId,
            meeting_password: this.teamsMeetingPassword
          },
          new_microsoft_graph_event_id: this.microsoftGraphEventInfo.newMicrosoftGraphEventId,
          old_microsoft_graph_event_id: this.microsoftGraphEventInfo.oldMicrosoftGraphEventId
        }
      }
      try {
        const resp = await this.updateInterviewRecordOnlineInfo(data)
        if (resp && resp === 'success') {
          const action = this.$t('common.btn.invite')
          this.$message({
            message: this.$t('common.message.actionSuccess', { action }),
            type: 'success'
          })
          this.microsoftGraphEventInfo.newMicrosoftGraphEventId = null
          this.show = false
          this.$emit('success')
        }
      } finally {
        this.loading = false
      }
    },
    handleCancel () {
      this.show = false
    },
    handleSendEmail () {
      this.showPreviewInterviewEmailDialog = false
      this.handleSave()
    },
    async handleClosedDialog () {
      this.invalidateEmailTemplateLoad()
      this.handleEnableOperateBtn()
    },
    async checkCurrentAccountAuthGraph () {
      if (this.hasMicrosoftGraphAuthToken || (this.microsoftGraphAuthWin && this.microsoftGraphAuthWin.closed)) {
        this.microsoftGraphAuthWin = null
        this.microsoftGraphAuthUrl = null
        return
      }
      const resp = await this.checkMicrosoftGraphAuth()
      if (resp) {
        this.hasMicrosoftGraphAuthToken = false
        if (!this.microsoftGraphAuthUrl) {
          this.microsoftGraphAuthUrl = resp
          this.microsoftGraphAuthWin = openNewBrowserWindowByUrl(this.microsoftGraphAuthUrl, null, 500, 600)
        }
        setTimeout(this.checkCurrentAccountAuthGraph, 1000)
      } else {
        this.microsoftGraphAuthUrl = null
        this.hasMicrosoftGraphAuthToken = true
        if (this.microsoftGraphAuthWin) {
          this.microsoftGraphAuthWin.close()
        }
      }
    },
    async createOrUpdateMicrosoftMeeting () {
      if (!this.hasMicrosoftGraphAuthToken) {
        await this.checkCurrentAccountAuthGraph()
      }

      const startDateTime = `${this.interviewedTime.startDate} ${this.interviewedTime.startTime}`
      const endDateTime = `${this.interviewedTime.endDate} ${this.interviewedTime.endTime}`

      const isOnlineMeeting = this.interviewType === INTERVIEW_TYPE_ONLINE_TEAMS_MEETING
      const params = {
        start_date_time: startDateTime,
        end_date_time: endDateTime,
        timezone: this.timeZone,
        hire_managers: this.selectHiringList,
        candidate_ids: _.map(this.applicationList, item => item.candidate.id),
        meeting_room_email: this.meetingRoom?.email,
        job_id: this.applicationList[0].job.id,
        is_online_meeting: isOnlineMeeting
      }
      let resp = null
      this.loading = true
      try {
        if (this.microsoftGraphEventInfo.newMicrosoftGraphEventId) {
          await this.deleteOnlineMeetingEvent({ eventId: this.microsoftGraphEventInfo.newMicrosoftGraphEventId })
        }
        if (this.microsoftGraphEventInfo.oldMicrosoftGraphEventId) {
          params.microsoft_graph_event_id = this.microsoftGraphEventInfo.oldMicrosoftGraphEventId
          resp = await this.updateOnlineMeetingEvent(params)
          this.microsoftGraphEventInfo.oldMicrosoftGraphEventId = null
        } else {
          resp = await this.createOnlineMeetingEvent(params)
        }

        if (resp) {
          const eventId = resp.id
          this.microsoftGraphEventInfo.newMicrosoftGraphEventId = eventId
          const meetingInfo = resp.meeting_info
          if (meetingInfo) {
            this.teamsMeetingUrl = meetingInfo.online_meeting?.joinUrl || ''
            this.teamsMeetingId = meetingInfo.meeting_id || ''
            this.teamsMeetingPassword = meetingInfo.passcode || ''
          }
        }
      } finally {
        this.loading = false
      }
    },
    resetTinymceDialogStyle () {
      // 由于插入 AI Summary 内容导致 tinymce 渲染在 body 上要等待 AI Summary 内容插入
      setTimeout(() => {
        const tinymceDialogs = document.querySelectorAll('.tox-tinymce-aux')
        if (tinymceDialogs && tinymceDialogs.length > 0) {
          _.forEach(tinymceDialogs, dialog => {
            dialog.style.zIndex = '99999999'
          })
        }
      }, 500)
    },
    handleSendHmLanguageChange () {
      if (!this.showHmEmailTemplate) {
        return
      }
      this.showHmEmailTemplate = false
      this.$nextTick(() => {
        if (!this.loadingHmTemplate) {
          this.showHmEmailTemplate = true
          this.resetTinymceDialogStyle()
        }
      })
    },
    handleGenerateAISummary (candidateId, jobId, languageCode, callback) {
      const application = _.find(this.applicationList, item => item.candidate.id === candidateId)
      const aiTask = {
        task: null,
        isFinished: false
      }
      let aiSummary = ''
      this.disabledHmOperateBtn = true
      aiTask.task = new AsyncTask(() => this.createAISummary({
        candidate_id: application.candidate.id,
        job_id: jobId,
        language_code: languageCode
      }), (taskId) => this.getAISummaryInfoByTaskId(taskId), (taskId, resp) => {
        let aiSummaryInfo
        if (resp) {
          aiSummaryInfo = resp.content
          aiSummary = languageCode === 'en' ? aiSummaryInfo.ai_summary_en : aiSummaryInfo.ai_summary_cn
          aiTask.isFinished = true
        }
        const content = {
          candidate_id: application.candidate.id,
          job_id: application.job.id,
          candidate_name: application.candidate.name,
          summary: aiSummary
        }
        if (aiSummaryInfo) {
          application.candidate.ai_summary_info = aiSummaryInfo
        }
        callback(content)
      })
    },
    handleGenerateAIMatch (candidateId, jobId, languageCode, callback) {
      const application = _.find(this.applicationList, item => item.candidate.id === candidateId)
      const aiTask = {
        task: null,
        isFinished: false
      }
      let aiMatchInfo = {}
      this.disabledHmOperateBtn = true
      aiTask.task = new AsyncTask(() => this.createAIResumeMatch({
        candidate_ids: [application.candidate.id],
        job_id: jobId,
        language_code: languageCode,
        await_result: true
      }), (taskId) => this.getAIMatchInfoByTaskId(taskId), (taskId, resp) => {
        if (resp) {
          aiTask.isFinished = true
          const result = resp.result
          const key = `${jobId}_${application.candidate.id}`
          aiMatchInfo = _.get(result, key)
          application.candidate.ai_match_info = aiMatchInfo
        }

        callback(aiMatchInfo)
      })
    },
    handleEnableOperateBtn () {
      this.disabledHmOperateBtn = false
    }
  },
  watch: {
    isSendToHmEmail () {
      this.previewInterviewEmailType = this.isSendToHmEmail ? 0 : 1
    }
  }
}
</script>

<style lang='scss' scoped>
.hr-interview-confirm-dialog {
  ::v-deep {
    .el-dialog {
      width: 60%;
    }

    .el-dialog__body {
      padding-left: 36px;
      padding-right: 46px;
    }
  }

  .hr-interview-confirm-form {
    word-break: initial;

    .step-content {
      margin-top: 25px;

      .content {
        .interview-model {
          border-bottom: 1px solid rgba(0, 0, 0, 0.2);
          display: flex;
          padding-bottom: 20px;

          .hm-model {
            flex: 1;
            border-radius: 4px;
            border: 1px solid #C2C5CB;
            padding: 16px 20px;

            .el-radio-group {
              margin-top: 10px;

              .el-radio {
                &.is-checked {
                  ::v-deep {
                    .el-radio__label {
                      color: #606266;
                    }
                  }
                }
              }
            }
          }

          .automate {
            flex: 1;
            border-radius: 4px;
            border: 1px solid #C2C5CB;
            padding: 16px 20px;
            margin-left: 20px;

            &.is-opacity {
              opacity: 0;
            }

            .el-checkbox {
              margin-top: 10px;

              &.is-checked {
                ::v-deep {
                  .el-checkbox__label {
                    color: #606266;
                  }
                }
              }
            }
          }

          .title {
            line-height: 20px;
            font-size: 14px;
            font-weight: 500;
            color: #303133;
          }
        }

        .hm {
          margin-top: 20px;
          border-bottom: 1px solid rgba(0,0,0,0.2);

          .hm-selector {
            width: 100%;
            padding-bottom: 20px;

            ::v-deep {
              .selector-content {
                margin-bottom: 0;

                .hm-email {
                  .el-select {
                    width: 100%;
                  }
                }

                .hm-last-name {
                  margin-right: 15px;
                }
              }

              .hm-list {
                font-size: 14px;
                margin-top: 20px;

                .hm {
                  height: 20px;
                  color: #666;
                  margin-bottom: 10px;
                }
              }

              .add-hm-btn, .delete-hm-btn {
                .icon-btn {
                  font-size: 20px;
                }
              }

              .el-input__inner {
                height: 30px;
                line-height: 30px;
              }

              .el-input__icon {
                line-height: 30px;
              }
            }
          }
        }

        .time {
          margin-top: 20px;
          border-bottom: 1px solid rgba(0,0,0,0.2);

          .time-type-content {
            display: flex;
            justify-content: space-between;
            margin-bottom: 20px;

            .time-zone {
              display: flex;
              align-items: center;

              .label {
                font-size: 12px;
              }

              .time-zone-selector {
                width: 292px;

                ::v-deep {
                  .el-input__inner {
                    border-radius: 2px;
                  }
                }
              }
            }
          }

          .time-schedule-content {
            display: flex;
            justify-content: space-between;
            margin-bottom: 20px;
          }

          .time-range-content {
            margin-bottom: 20px;

            .interview-time-range {
              & + .interview-time-range {
                margin-top: 10px;
              }
            }
          }
        }

        .location {
          display: flex;
          flex-direction: column;
          margin-top: 20px;

          .title {
            margin-bottom: 14px;
          }

          .location-select {
            position: relative;
            flex: 1;

            ::v-deep .el-radio-group {
              .el-radio-button__inner {
                padding: 8px 20px;
              }

              .is-active {
                .el-radio-button__inner {
                  color: #0096CC;
                  background: #FFFFFF;
                }
              }
            }

            .el-input {
              margin-top: 20px;
            }

            .el-select {
              margin-top: 20px;
            }

            .error {
              padding-top: 4px;
              color: #f56c6c;
              font-size: 12px;
              line-height: 1;
            }

            .teams-meeting {
              margin-top: 6px;
              font-size: 12px;
              color: #999;
              line-height: 18px;
            }
          }
        }

        .content-title {
          line-height: 20px;
          font-size: 14px;
          color: #303133;
          font-weight: 500;
          margin-bottom: 10px;
        }
      }

      .invitation {
        margin-top: 10px;

        .label {
          display: flex;
          align-items: center;
          justify-content: space-between;

          .to-select-language {
            display: flex;
            align-items: center;
            .send-type {
              display: block;
              margin-right: 14px;
            }

            .language-title {
              display: block;
              margin-right: 14px;
            }

            .language-select {
              flex: 1;
            }
          }
        }

        .invitation-subject {
          display: flex;
          align-items: center;
          margin-top: 16px;
          margin-bottom: 16px;

          .subject-label {
            width: 80px;
            font-size: 18px;
            font-weight: 400;
            color: #606266;
          }
          ::v-deep {
            .el-input {
              flex: 1;
              margin-left: 12px;
              width: auto;
            }
          }
        }

        .invitation-content{
          padding-left: 92px;
        }
      }
    }

    .content-label {
      margin-bottom: 10px;
      font-size: 18px;
      font-weight: 500;
      color: #303133;
    }

    .microsite-select-container {
      display: flex;
      align-items: center;

      .microsite-label {
        width: 80px;
        font-size: 18px;
        font-weight: 400;
        color: #606266;
      }

      .microsite-select {
        flex: 1;
        margin-left: 12px;
      }
    }
  }

  .dialog-footer {
    .footer-btn  {
      border-radius: 2px;
      padding: 10px 30px;
    }

    .save-btn {
      background: #0096cc;
      border-color: #0096cc;
      color: #ffffff;
    }

    .cancel-btn {
      background: #ffffff;
      color: #606266;
    }
  }
}

.preview-interview-email-dialog {
  overflow: hidden;

  ::v-deep {
    .el-dialog {
      margin-top: 0;
      height: 100%;
      overflow: hidden;

      .el-dialog__body {
        height: 94%;
        overflow: hidden;
        word-break: break-word;
        white-space: normal;
      }
    }
  }

  .header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    .select-preview-type {
      display: inline-block;
      width: 12%;
    }

    .operation-btn {
      display: inline-block;
      .footer-btn  {
        border-radius: 2px;
        padding: 10px 30px;
      }

      .save-btn {
        background: #0096cc;
        border-color: #0096cc;
        color: #ffffff;
      }

      .cancel-btn {
        background: #ffffff;
        color: #606266;
      }

    }
  }

  .preview-content {
    margin-top: 10px;
    min-height: 500px;
    overflow-y: auto;
    max-height: 90%;
    ::v-deep {
      #custom-tag-candidate_ai_summary {
        .candidate-ai-summary {
          .candidate-summary-item {
            .rate-btn {
              cursor: not-allowed;
              a {
                cursor: not-allowed;
                pointer-events: none;
              }
            }
          }
        }
        .view-dashboard-btn {
          cursor: not-allowed;
          a {
            cursor: not-allowed;
            pointer-events: none;
          }
        }
      }
    }
  }
}

.hm-status {
  font-size: 18px;
  left: 10px;
  position: absolute;
  display: inline-block;
  color: #9ad792;

  &.is-busy {
    color: #e76f78;
  }

  & + .hm-label {
    padding-left: 10px;
  }
}
</style>
